import { Badge } from '@/components/ui/badge';
import { Dot } from '@/components/ui/dot';
import { useDronesStore } from '@/lib/store/drones';
import type { DroneLive } from '@/lib/store/drones';
import { flightStateBadge } from '@/lib/store/drones';
import { visibleDroneTab } from '@/lib/droneContextFlags';
import { useUiStore } from '@/lib/store/ui';
import { ArrowLeft } from 'lucide-react';
import { useShallow } from 'zustand/shallow';
import { TabStrip } from './TabStrip';
import { AgentsTab } from './tabs/Agents';
import { C2Tab } from './tabs/C2';
import { EventsTab } from './tabs/Events';
import { TelemetryTab } from './tabs/Telemetry';
import { FlightOpsTab } from './tabs/FlightOps';
import { Overview } from './tabs/Overview';
import { ProximityTab } from './tabs/Proximity';
import { SurveillanceTab } from './tabs/Surveillance';
import { TerrainTab } from './tabs/Terrain';
import { WeatherTab } from './tabs/Weather';

/** Pipeline delay for the header readout. Sub-second lag is the normal case and
 *  reads better in ms; anything slower is the interesting case and reads better
 *  in seconds. Returns null when the publisher sends no value, so the caller can
 *  omit the readout rather than render a "—" that looks like a broken metric. */
function fmtPipelineDelay(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
  const ms = seconds * 1000;
  return ms < 1000 ? `${Math.round(ms)} ms` : `${seconds.toFixed(1)} s`;
}

export function DroneDetailPanel({ id }: { id: string }) {
  const drone = useDronesStore(useShallow((s) => s.byId[id] ?? null));
  const select = useDronesStore((s) => s.select);
  // ENG-271: a tab saved from an earlier session may now be hidden.
  const tab = visibleDroneTab(useUiStore((s) => s.activeTab));

  if (!drone) return null;
  const t = drone.telemetry;
  // Every aircraft gets the same tab panel regardless of provenance.
  // Cross-tenant aircraft arrive on the same topics with the same payloads,
  // so a separate reduced layout only hid data we already had. Origin now
  // changes labelling only — the extra `Partner` / `Remote Identification`
  // sections in Overview.
  const external = drone.origin !== 'internal';
  const flightBadge = flightStateBadge(t);

  const updateAgeSec = Math.max(0, Math.round((Date.now() - drone.received_at_ms) / 1000));
  const stalenessTone =
    drone.staleness === 'lost' ? 'crit' : drone.staleness === 'stale' ? 'warn' : 'ok';

  // Backend-reported end-to-end pipeline lag (`telemetry.timestamp_delay`), which
  // beats deriving lag from our own clock. Live on sandbox at ~0.1 s; null on
  // publishers that don't send it, in which case the readout is omitted entirely.
  //
  // Deliberately uncoloured: Sprint 30 Workstream F has yet to agree what
  // "real-time" means numerically, and a red/amber threshold invented here would
  // assert a standard nobody signed off. Add the tone once the bar is set.
  const pipelineDelay = fmtPipelineDelay(drone.telemetry.pipeline_delay_s);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <button
          type="button"
          onClick={() => select(null)}
          className="flex items-center gap-1 text-(--color-green) text-[0.846em] font-semibold mb-1 hover:opacity-80"
        >
          <ArrowLeft size={13} /> Drones
        </button>
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-[1.077em] font-bold tracking-tight">{t.callsign}</h2>
          {external ? (
            <Badge variant="muted">{drone.origin === 'partner' ? 'Partner' : 'Network ID'}</Badge>
          ) : (
            flightBadge && <Badge variant={flightBadge.variant}>{flightBadge.label}</Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[0.692em] font-mono text-(--color-text3)">
          <Dot variant={stalenessTone} size="xs" pulse={stalenessTone === 'ok'} />
          {external && drone.origin === 'partner'
            ? `Partner: ${drone.partnerTenant ?? '—'} · `
            : ''}
          {external ? 'Last broadcast' : 'Last update'}: {updateAgeSec}s ago
          {pipelineDelay && (
            <span title="Backend-reported end-to-end pipeline delay (telemetry.timestamp_delay) — time from source to broker, not our render time">
              {' · '}
              pipeline {pipelineDelay}
            </span>
          )}
        </div>
      </header>

      <TabStrip />
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <TabBody tab={tab} drone={drone} />
      </div>
    </div>
  );
}

function TabBody({
  tab,
  drone,
}: { tab: ReturnType<typeof useUiStore.getState>['activeTab']; drone: DroneLive }) {
  switch (tab) {
    case 'ldt-status':
      return <Overview drone={drone} />;
    case 'ldt-daa':
      return <SurveillanceTab drone={drone} />;
    case 'ldt-telemetry':
      return <TelemetryTab drone={drone} />;
    case 'ldt-c2':
      return <C2Tab drone={drone} />;
    case 'ldt-log':
      return <EventsTab drone={drone} />;
    case 'ldt-proximity':
      return <ProximityTab drone={drone} />;
    case 'ldt-agent':
      return <AgentsTab drone={drone} />;
    case 'ldt-terrain':
      return <TerrainTab drone={drone} />;
    case 'ldt-flight':
      return <FlightOpsTab drone={drone} />;
    case 'ldt-weather':
      return <WeatherTab lat={drone.telemetry.lat} lng={drone.telemetry.lng} />;
    default:
      return null;
  }
}
